import type { Board } from 'chessboard/model/board/types';
import { Core } from 'chessboard/core/config/types';
import { StateMachine } from 'chessboard/core/state/config/types';
import type JCE from '@chesscom/js-chess-engine';
import type { Move } from '@chesscom/js-chess-engine';
import { deduplicate } from 'src/utils/array/deduplicate';
import { Game } from '../game/types';

/**
 * Update hints on the board.
 */
export function updateHints({
  addMoveHints,
  addCaptureHints,
  context,
  currentHints,
  getNextHints,
  removeHints,
}: {
  addMoveHints: (hints: Board.SanSquare[], context: Core.Context) => void;
  addCaptureHints?: (hints: Board.SanSquare[], context: Core.Context) => void;
  context: Core.Context;
  currentHints: Board.SanSquare[];
  getNextHints: (legalMoves: Board.SanSquare[]) => Board.SanSquare[];
  removeHints: (hints: Board.SanSquare[], context: Core.Context) => void;
}): Board.SanSquare[] {
  let updatedHints = currentHints;

  const { api, mode, options, state } = context;

  if (!options || !options.highlightLegalMoves || !mode.isAllowedToMove()) {
    removeHints([...currentHints], context);

    updatedHints.length = 0;

    return updatedHints;
  }

  const selectedSquare: JCE.SanSquare | undefined =
    state.get(Core.State.SelectedPiece) || state.get(Core.State.DraggingPiece);

  if (!selectedSquare) {
    if (updatedHints.length) {
      removeHints(updatedHints, context);

      updatedHints = [];
    }

    return updatedHints;
  }

  const legalMovesObjs =
    state.get(Core.State.MachineState) === StateMachine.States.PromotionAreaShowing
      ? []
      : api.getLegalMoves().filter((gameMove: Move.Model) => gameMove.from === selectedSquare);

  const hintSquares = deduplicate(legalMovesObjs.map((gameMove: Move.Model) => gameMove.to));

  if (!options.disableRookCastling) {
    hintSquares.push(...getExtraCastlingSquares(legalMovesObjs));
  }

  const remove = updatedHints.filter((square) => !hintSquares.includes(square));

  removeHints(remove, context);

  const hintsToAdd = getNextHints(hintSquares);

  const moveHints: Board.SanSquare[] = [];
  const captureHints: Board.SanSquare[] = [];

  const pieces = api.getPieces().keys();

  hintsToAdd.forEach((square) => {
    if (addCaptureHints && pieces.includes(square)) {
      captureHints.push(square);
    } else {
      moveHints.push(square);
    }
  });

  if (moveHints.length) {
    addMoveHints(moveHints, context);
  }

  if (captureHints.length) {
    addCaptureHints?.(captureHints, context);
  }

  return updatedHints.filter((hint) => !remove.includes(hint)).concat(hintsToAdd);

  /**
   * Gets additional castling squares such as clicking on king->rook.
   */
  function getExtraCastlingSquares(legalMovesForPiece: Move.Model[]): JCE.SanSquare[] {
    if (
      // 960 already returns king->rook as a legal move, so we can return early
      api.getVariant() === Game.Variants.Chess960 ||
      legalMovesForPiece.length === 0 ||
      legalMovesForPiece[0].from === undefined
    ) {
      return [];
    }

    const piecesData = api.getPieces().getCollection();
    const king = piecesData[legalMovesForPiece[0].from];
    if (king?.type !== 'k') {
      return [];
    }

    // Returns squares of relevent rooks if they are on expected squares for castle moves.
    return legalMovesForPiece.reduce((acc, move) => {
      let rookSquare: JCE.SanSquare;

      // assume rooks are on a1/a8 or h1/h8, since we exited early for 960 above
      if (move.san === 'O-O-O') {
        rookSquare = move.color === Game.ColorsAsNumbers.White ? 'a1' : 'a8';
      } else if (move.san === 'O-O') {
        rookSquare = move.color === Game.ColorsAsNumbers.White ? 'h1' : 'h8';
      } else {
        return acc;
      }

      const rook = piecesData[rookSquare];
      // verify relevant rook is on the expected square, for extra safety
      if (
        rook?.type.toLowerCase() === 'r' &&
        rook?.color === king.color &&
        !rook?.promoted &&
        move.to !== rookSquare // avoid adding duplicate hint just in case
      ) {
        acc.push(rookSquare);
      }

      return acc;
    }, [] as JCE.SanSquare[]);
  }
}
